
'use client';

import { useState, useMemo, useRef } from 'react';
import { JartinetHermandad, UserProfile } from '@/lib/types';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { sendPapeleta } from '@/lib/actions';
import { PapeletaDeSitioPreview } from './PapeletaDeSitio';
import { DialogFooter, DialogClose } from '../ui/dialog';

interface CreatePapeletaFormProps {
  hermandad: JartinetHermandad;
  allHermanos: UserProfile[];
  onFormSuccess: () => void;
  onClose: () => void;
}

export function CreatePapeletaForm({ hermandad, allHermanos, onFormSuccess, onClose }: CreatePapeletaFormProps) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para la vista previa en tiempo real
  const [previewData, setPreviewData] = useState({
    hermanoId: '',
    diaDeSalida: hermandad.dia || '',
    cargo: '',
    hora: '',
    ornamentColor: hermandad.colorPrimario,
    titleColor: hermandad.colorPrimario,
    selloColor: hermandad.colorPrimario,
    selloOpacity: 0.2, // Valor por defecto
  });
  
  const selectedHermano = useMemo(() => allHermanos.find(h => h.id === previewData.hermanoId) || null, [previewData.hermanoId, allHermanos]);

  const handlePreviewChange = (field: keyof typeof previewData, value: string | number) => {
    setPreviewData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!previewData.hermanoId) {
        toast({ title: "Error", description: "Debes seleccionar un hermano.", variant: "destructive" });
        return;
    }
    setIsSubmitting(true);
    
    // Usamos los datos del estado de la preview para enviar
    const result = await sendPapeleta(hermandad.id, previewData);
    
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      formRef.current?.reset();
      setPreviewData({ // Reset preview after successful submission
          hermanoId: '',
          diaDeSalida: hermandad.dia || '',
          cargo: '',
          hora: '',
          ornamentColor: hermandad.colorPrimario,
          titleColor: hermandad.colorPrimario,
          selloColor: hermandad.colorPrimario,
          selloOpacity: 0.2,
      });
      onFormSuccess();
      onClose(); // Close dialog on success
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };
  
  return (
    <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8 p-1">
      {/* Columna de Vista Previa */}
      <div className="flex flex-col items-center gap-4">
        <h3 className="font-semibold text-lg">Vista Previa</h3>
        <div className="w-full max-w-sm">
            <PapeletaDeSitioPreview 
                hermandad={hermandad}
                hermano={selectedHermano}
                diaDeSalida={previewData.diaDeSalida}
                cargo={previewData.cargo}
                hora={previewData.hora}
                fechaCreacion={new Date()}
                ornamentColor={previewData.ornamentColor}
                titleColor={previewData.titleColor}
                selloColor={previewData.selloColor}
                selloOpacity={previewData.selloOpacity}
            />
        </div>
      </div>
      
      {/* Columna de Formulario */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="hermanoId">Enviar a Hermano</Label>
          <Select name="hermanoId" value={previewData.hermanoId} onValueChange={(value) => handlePreviewChange('hermanoId', value)}>
            <SelectTrigger><SelectValue placeholder="Elige un hermano..." /></SelectTrigger>
            <SelectContent>
              {allHermanos.map(h => <SelectItem key={h.id} value={h.id}>{h.nickname || h.id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cargo">Cargo en la Cofradía</Label>
          <Input id="cargo" name="cargo" placeholder="Ej: Cirio, Vara, Presidencia..." value={previewData.cargo} onChange={(e) => handlePreviewChange('cargo', e.target.value)} />
        </div>
         <div className="space-y-2">
            <Label htmlFor="diaDeSalida">Día de Salida</Label>
            <Input id="diaDeSalida" name="diaDeSalida" placeholder="Ej: Domingo de Ramos" value={previewData.diaDeSalida} onChange={(e) => handlePreviewChange('diaDeSalida', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="hora">Hora de Presentación</Label>
          <Input id="hora" name="hora" type="time" value={previewData.hora} onChange={(e) => handlePreviewChange('hora', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
                <Label htmlFor="ornamentColor">Color de Ornamento</Label>
                <Select name="ornamentColor" value={previewData.ornamentColor} onValueChange={(v) => handlePreviewChange('ornamentColor', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={hermandad.colorPrimario}>Primario</SelectItem>
                        <SelectItem value={hermandad.colorSecundario}>Secundario</SelectItem>
                    </SelectContent>
                </Select>
            </div>
             <div className="space-y-2">
                <Label htmlFor="titleColor">Color del Título</Label>
                <Select name="titleColor" value={previewData.titleColor} onValueChange={(v) => handlePreviewChange('titleColor', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={hermandad.colorPrimario}>Primario</SelectItem>
                        <SelectItem value={hermandad.colorSecundario}>Secundario</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>
         <div className="space-y-2">
            <Label htmlFor="selloColor">Color del Sello</Label>
            <Select name="selloColor" value={previewData.selloColor} onValueChange={(v) => handlePreviewChange('selloColor', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={hermandad.colorPrimario}>Primario</SelectItem>
                    <SelectItem value={hermandad.colorSecundario}>Secundario</SelectItem>
                </SelectContent>
            </Select>
        </div>
        <DialogFooter className="md:col-span-2 pt-4">
           <DialogClose asChild>
                <Button variant="ghost" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enviar Papeleta de Sitio
            </Button>
        </DialogFooter>
      </div>
    </form>
  );
}
